import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { authTokenService } from '@/bundle/shared/services/authTokenService';
import { getLoginPath } from '@/bundle/Auth/LoginFlow/LoginPage/path/path';
import { sessionStorageService } from '@/helpers/storageHelpers';
import { REDIRECT_URL_KEY, TOKEN_REFRESHED_EVENT } from '@/const/shared';
import { ORG_ADMIN_ROLE, USER_ROLE_MAP, UserRoleType } from '@/const/user';
import {
  getFirstRole,
  getOrganizationRole,
  getStorageCurrentRole,
  hasMultipleRoles,
  setStorageCurrentRole,
} from '@/helpers/userHelpers';
import { SelfUserDetailsType } from '@/api/v1/users/getSelfUserDetailsApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { loadSelfUserDetails } from '@/bundle/shared/api';
import { isTrialMode } from '@/helpers/billingHelpers';

interface UserContextType {
  user: SelfUserDetailsType;
  loading: boolean;
  currentRole: UserRoleType;
  hasMultipleUserRoles: boolean;
  setRole: (role: UserRoleType) => void;
  refetchUser: () => Promise<void>;
  clearUser: () => void;
  hasOrganizationRole: boolean;
  isOrgAdminRole: boolean;
  isMasterAdminRole: boolean;
  isAdminRole: boolean;
  isOpcoRole: boolean;
  isExecutorRole: boolean;
  isTrialAdmin: boolean;
}

const UserContextProvider = createContext<UserContextType>({} as UserContextType);

export const UserProvider = ({ children }: { children?: ReactNode }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<SelfUserDetailsType>(null);
  const [hasMultipleUserRoles, setHasMultipleUserRoles] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const tokens = authTokenService.getTokens();

  const { data: userData, isPending } = useQuery({
    queryKey: ['load_self_user'],
    queryFn: () => loadSelfUserDetails(),
    enabled: !!tokens?.user_id,
  });

  const setRole = (role: UserRoleType) => {
    setCurrentUserRole(role);
    setStorageCurrentRole(role);
  };

  const clearUser = () => {
    setUser(null);
  };

  const getRole = () => {
    if (currentUserRole) {
      return currentUserRole;
    }

    const role = getStorageCurrentRole();

    if (role) {
      setRole(role);
    }

    return role;
  };

  const refetchUser = () => {
    return queryClient.invalidateQueries({ queryKey: ['load_self_user'] });
  };

  useEffect(() => {
    if (!tokens?.user_id) {
      sessionStorageService.set(REDIRECT_URL_KEY, location);

      navigate(getLoginPath());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userData?.body) {
      setUser(userData?.body);

      setHasMultipleUserRoles(hasMultipleRoles(userData?.body?.active_roles));
      amplitudeService.setUser(userData.body, currentUserRole);

      const activeRoles = userData?.body?.active_roles;

      if (activeRoles[currentUserRole]) return;

      // Handle case when current role is not exists in active roles and we switch to available one.
      if (activeRoles.organization_user) {
        setRole(activeRoles.organization_user);
      } else {
        setRole(getFirstRole(activeRoles));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.body]);

  useEffect(() => {
    if (userData?.error) {
      navigate(getLoginPath());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.error]);

  useEffect(() => {
    // Load user details whenever access token updated to keep user roles up to date.
    window.addEventListener(TOKEN_REFRESHED_EVENT, refetchUser);

    return () => {
      window.removeEventListener(TOKEN_REFRESHED_EVENT, refetchUser);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasOrganizationRole = !!getOrganizationRole(user);
  const isOrgAdminRole = currentUserRole === ORG_ADMIN_ROLE.ORG_ADMIN;
  const isMasterAdminRole = currentUserRole === ORG_ADMIN_ROLE.MASTER_ADMIN;
  const isAdminRole = isOrgAdminRole || isMasterAdminRole;
  const isOpcoRole = currentUserRole === USER_ROLE_MAP.OPCO;
  const isExecutorRole = currentUserRole === USER_ROLE_MAP.EXECUTOR;
  const isTrialAdmin = isTrialMode(user);

  return (
    <UserContextProvider.Provider
      value={{
        user,
        loading: isPending,
        currentRole: getRole(),
        hasMultipleUserRoles,
        setRole,
        refetchUser,
        clearUser,
        hasOrganizationRole,
        isOrgAdminRole,
        isMasterAdminRole,
        isAdminRole,
        isOpcoRole,
        isExecutorRole,
        isTrialAdmin,
      }}
    >
      {children ? children : <Outlet />}
    </UserContextProvider.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContextProvider);
};
